import { MagnifyingGlass, Stop } from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { Button } from '~/components/ui/button';
import { Badge } from '~/components/ui/badge';
import { useSkillRunStatus } from '~/hooks/queries/use-inspector';
import { useResearchByRunId, useResearchCitations } from '~/hooks/queries/use-research';
import { useCancelResearch } from '~/hooks/mutations/use-cancel-research';
import { useSecondTicker } from '~/hooks/use-second-ticker';
import { formatElapsed } from '~/lib/elapsed';

interface SkillProgressDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The run's skillRunId (e.g. `plan-86-…` / `deep-research-…`). */
  runId: string;
  /** Speaking headline (research topic / plan title). */
  title?: string;
  /** Row's startedAt (epoch ms) so elapsed renders before status loads. */
  startedAtFallback?: number;
}

/**
 * FRONT-719 — the live counterpart to SkillResultDialog / SkillFailureDialog.
 * A *running* research/plan card is now clickable so the user can see what it's
 * doing instead of staring at a frozen "Planning" line. Read-only status
 * (phase + elapsed + sources + last error) plus a Stop button, all from data
 * the card already polls. The 1s ticker keeps the elapsed time honest without
 * waiting on the 2–3s network poll.
 */
export function SkillProgressDialog({
  open,
  onOpenChange,
  runId,
  title,
  startedAtFallback,
}: SkillProgressDialogProps) {
  const { data: status } = useSkillRunStatus(runId, open);
  const { data: run } = useResearchByRunId(open ? runId : undefined);
  const matchingRun = run && run.skillRunId === runId ? run : null;
  const { data: citations } = useResearchCitations(matchingRun?.id, {
    enabled: open && !!matchingRun,
    live: open,
  });
  const cancel = useCancelResearch();
  useSecondTicker(open);

  const startMs = status?.startedAt ? new Date(status.startedAt).getTime() : startedAtFallback;
  const phase = status?.lastToolCall || 'Starting…';
  const sourceCount = citations?.length ?? 0;
  const lastError = status?.error?.trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader className="min-w-0">
          <DialogTitle className="flex items-center gap-2 min-w-0">
            <MagnifyingGlass size={18} className="text-muted-foreground shrink-0" />
            <span className="text-base font-medium truncate">
              {matchingRun?.topic || title || 'Deep research'}
            </span>
            <Badge
              variant="secondary"
              className="ml-1 shrink-0 border-amber-500/30 text-amber-600 dark:text-amber-400"
            >
              Running
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <section className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-sm">
          <span className="text-muted-foreground">Phase</span>
          <span className="truncate">{phase}</span>
          <span className="text-muted-foreground">Elapsed</span>
          <span className="tabular-nums">{formatElapsed(startMs)}</span>
          {matchingRun && (
            <>
              <span className="text-muted-foreground">Sources</span>
              <span className="tabular-nums">
                {sourceCount} {sourceCount === 1 ? 'source' : 'sources'}
              </span>
            </>
          )}
        </section>

        {lastError && (
          <section>
            <div className="text-xs uppercase tracking-wider text-muted-foreground/70 mb-1.5">
              Last error
            </div>
            <div className="rounded border border-destructive/30 bg-destructive/5 p-2.5 text-xs text-destructive break-words [overflow-wrap:anywhere]">
              {lastError}
            </div>
          </section>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          {matchingRun ? (
            <Button
              variant="outline"
              className="text-destructive hover:text-destructive"
              disabled={cancel.isPending}
              onClick={() => cancel.mutate({ runId: matchingRun.id }, { onSuccess: () => onOpenChange(false) })}
            >
              <Stop size={14} weight="fill" className="mr-1.5" />
              Stop research
            </Button>
          ) : (
            <span />
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
